import React, { useEffect, useState } from 'react';
import { ArrowLeft, CheckCircle2, TrendingDown, TrendingUp } from 'lucide-react';

import { fetchMyConfirmationRate } from '../api';
import { ConfirmationRateClass } from '../types';

interface MyConfirmationRateProps {
  onBack: () => void;
}

const WINDOW_OPTIONS = [30, 90, 180];

/** How much the confirmation rate colours as "good" vs "worth a look" — a reviewer's own signal,
 *  not a threshold the engine acts on anywhere (§13.5 U15's own framing: this validates the
 *  engine from a reviewer's decisions, it does not feed back into them). */
const rateTone = (rate: number): string =>
  rate >= 0.85 ? 'text-emerald-400' : rate >= 0.6 ? 'text-amber-400' : 'text-rose-400';

const ClassRow: React.FC<{ row: ConfirmationRateClass }> = ({ row }) => (
  <tr>
    <td className="p-3 font-semibold text-white">{row.rhythm_class}</td>
    <td className="p-3 text-right mono text-slate-300">{row.reviewed}</td>
    <td className="p-3 text-right mono text-emerald-300">{row.confirmed}</td>
    <td className="p-3 text-right mono text-rose-300">{row.rejected}</td>
    <td className="p-3 text-right mono text-amber-300">{row.reclassified}</td>
    <td className={`p-3 text-right mono font-bold ${rateTone(row.confirmation_rate)}`}>
      {(row.confirmation_rate * 100).toFixed(0)}%
    </td>
  </tr>
);

/**
 * A reviewer's own agreement with the engine (DESIGN.md §13.5 U15, §17.6 M3).
 *
 * The data has existed since every verdict started writing a `review_labels` row and the nightly
 * drift report started aggregating it fleet-wide — this is the same computation from one
 * reviewer's own point of view, the piece U15 named as the only part still missing: "no screen
 * shows a reviewer their own confirmation rate per class."
 */
export const MyConfirmationRate: React.FC<MyConfirmationRateProps> = ({ onBack }) => {
  const [windowDays, setWindowDays] = useState(90);
  const [classes, setClasses] = useState<ConfirmationRateClass[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchMyConfirmationRate(windowDays)
      .then((data) => {
        setClasses(data.classes);
        setError(null);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load your confirmation rate.'))
      .finally(() => setLoading(false));
  }, [windowDays]);

  const totalReviewed = classes?.reduce((sum, c) => sum + c.reviewed, 0) ?? 0;
  const totalConfirmed = classes?.reduce((sum, c) => sum + c.confirmed, 0) ?? 0;
  const overallRate = totalReviewed > 0 ? totalConfirmed / totalReviewed : null;

  return (
    <div className="mx-auto max-w-3xl space-y-4 px-4 py-6">
      <div className="glass-panel flex items-center justify-between rounded-2xl p-4">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="rounded-xl border border-slate-800 bg-slate-900 p-2 text-slate-300 hover:text-white">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h2 className="text-lg font-bold text-white">My confirmation rate</h2>
            <p className="text-xs text-slate-400">
              How often you confirmed, rejected or reclassified what the engine proposed, per class.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 rounded-lg border border-slate-800 bg-slate-900 p-1 text-xs">
          {WINDOW_OPTIONS.map((days) => (
            <button
              key={days}
              onClick={() => setWindowDays(days)}
              className={`rounded-md px-2.5 py-1 font-semibold transition-all ${
                windowDays === days ? 'bg-cyan-800 text-white' : 'text-slate-400 hover:text-white'
              }`}
            >
              {days}d
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-rose-800 bg-rose-950 px-4 py-2 text-xs text-rose-200">{error}</div>
      )}

      {overallRate !== null && (
        <div className="glass-panel flex items-center gap-4 rounded-2xl p-4">
          <div className={`flex h-12 w-12 items-center justify-center rounded-full border-2 ${
            overallRate >= 0.85 ? 'border-emerald-700 bg-emerald-950' : 'border-amber-700 bg-amber-950'
          }`}>
            {overallRate >= 0.85 ? (
              <TrendingUp className="h-5 w-5 text-emerald-400" />
            ) : (
              <TrendingDown className="h-5 w-5 text-amber-400" />
            )}
          </div>
          <div>
            <div className={`text-2xl font-bold ${rateTone(overallRate)}`}>
              {(overallRate * 100).toFixed(0)}%
            </div>
            <div className="text-xs text-slate-400">
              overall, across {totalReviewed} finding{totalReviewed === 1 ? '' : 's'} reviewed in the last {windowDays} days
            </div>
          </div>
        </div>
      )}

      <div className="glass-panel overflow-hidden rounded-2xl">
        <table className="w-full text-left text-xs">
          <thead className="border-b border-slate-800 bg-slate-900/80 uppercase text-[10px] tracking-wider text-slate-400">
            <tr>
              <th className="p-3">Class</th>
              <th className="p-3 text-right">Reviewed</th>
              <th className="p-3 text-right">Confirmed</th>
              <th className="p-3 text-right">Rejected</th>
              <th className="p-3 text-right">Reclassified</th>
              <th className="p-3 text-right">Confirmation rate</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/60">
            {loading && (
              <tr><td colSpan={6} className="p-6 text-center text-slate-400">Loading…</td></tr>
            )}
            {!loading && classes && classes.length === 0 && (
              <tr>
                <td colSpan={6} className="p-6 text-center text-slate-400">
                  <div className="flex flex-col items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-slate-600" />
                    No verdicts recorded against your account in the last {windowDays} days yet.
                  </div>
                </td>
              </tr>
            )}
            {classes?.map((row) => <ClassRow key={row.rhythm_class} row={row} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
};
